import { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useCoinsAnimation } from '../../hooks/useCoinsAnimation';
import { resolveFlag } from '../../services/launchDarkly';
import { useStores } from '../../stores/useStores';
import { devError, devLog } from '../../utils/log';
import { Loader } from '../Loader/Loader';

import styles from './MetagamesWarmFrame.module.scss';

/**
 * A metagames-only game-ui iframe, booted before the player clicks so opening a metagame costs no boot.
 *
 * Wired for all four metagames: Prize Drop, Trail, Hammers and Puzzle. The warm trigger is lobby load
 * rather than a widget entering the viewport — see
 * game-ui/docs/superpowers/specs/2026-08-12-metagame-warm-iframe-design.md.
 */
/** Forces warm on without touching the flag, for testing. Drop once the rollout is settled. */
const WARM_LAUNCH_OVERRIDE_PARAM = 'warmLaunch';

export const MetagamesWarmFrame = observer(() => {
  const {
    metagamesWarmStore,
    prizeDropStore,
    trailStore,
    hammersStore,
    puzzleStore,
    metagamesStore,
    walletStore,
    minigamesStore,
    gameUiStore,
  } = useStores();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { startAnimation } = useCoinsAnimation();
  // Single owner of the loader: two components deciding visibility is what doubled it last time.
  const [isGameReady, setIsGameReady] = useState(false);

  const onMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== import.meta.env.VITE_GAME_UI_URL) {
        return;
      }

      // Only this frame's own messages. Every metagame dialog listens on window, so without this the
      // warm frame and a mounted dialog would both act on the same message.
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) {
        return;
      }

      try {
        const message = typeof event.data === 'object' ? event.data : JSON.parse(event.data);
        const { type, data, payload } = message ?? {};

        if (type === 'warmReady') {
          devLog('MetagamesWarmFrame: warm frame reported ready');
          // event.source is the window that just told us it is ready — the only window we should ever
          // post a launch into.
          metagamesWarmStore.onWarmReady(event.source as Window | null);
          return;
        }

        // Prize Drop speaks `data.action`, unlike Trail/Hammers/Puzzle which use `type`. Handling only
        // its close here is what makes repeated measurement runs possible.
        // Prize Drop reports readiness as `data.action: 'loaded'`; the other three use
        // `type: 'authenticationComplete'`. Accept both so this survives being pointed at another game.
        if (data?.action === 'loaded' || type === 'authenticationComplete') {
          setIsGameReady(true);
          return;
        }

        // Trail and Puzzle launch their minigame through this, and it needs the iframe ELEMENT, not the
        // window — which is why the warm frame has to own it rather than relay the message. With no
        // dialog mounted, starting a minigame from inside Trail otherwise did nothing at all.
        // Hammers only, and only its dialog handles this — so on the warm path the reward animation
        // would simply never play.
        if (type === 'coinsAnimation') {
          const scAmount = payload?.find((r: { coinType: string }) => r.coinType === 'SC')?.coinValue || 0;
          const gcAmount = payload?.find((r: { coinType: string }) => r.coinType === 'GC')?.coinValue || 0;

          startAnimation({ rewards: [{ scCoin: Number(scAmount), gcCoin: Number(gcAmount), bundleItems: [] }] });
          return;
        }

        if (type === 'startMinigame') {
          minigamesStore.startMetagameMinigame(message.payload?.gameId, iframeRef.current);
          return;
        }

        if (type === 'vibrate') {
          window.navigator.vibrate?.(50);
          return;
        }

        // Trail closes with `type: 'closeGame'`, Prize Drop with `data.action: 'close'`. Close the game
        // that is actually live rather than assuming — activeKey is the only thing that knows.
        const isClose =
          data?.action === 'close' ||
          data?.action === 'load_timeout' ||
          type === 'closeGame' ||
          type === 'generalError';

        if (isClose) {
          setIsGameReady(false);

          // The dialogs do more than close on this message: Trail/Hammers/Puzzle refetch event state and
          // the wallet, because a metagame can change the balance. Skipping that left a stale balance on
          // screen after playing — a money-visible bug, and silent.
          void metagamesStore.fetch();
          void walletStore.fetchWallet();

          // The close analytics event is sent by the dialog handler, not by closeGame() — verified. On the
          // warm path no dialog is mounted, so without this every warm close is missing from analytics,
          // and the better warm launch works the more of that data silently disappears.
          const closeAnalytics = { click_location: 'close', event_action: 'click', triggered_by: 'click' };

          // Only tell the game to close. Its own closeGame drives the frame back to idle, so every exit
          // path — including errors and programmatic closes that post no message — goes through one place.
          switch (metagamesWarmStore.activeKey) {
            case 'trail':
              trailStore.sendAnalyticsEvent(closeAnalytics);
              trailStore.closeGame();
              break;
            case 'puzzle':
              puzzleStore.sendAnalyticsEvent(closeAnalytics);
              puzzleStore.closeGame();
              break;
            case 'hammers':
              // Its dialog sends no close analytics, so neither do we.
              hammersStore.closeGame();
              break;
            case 'prizeDrop':
              prizeDropStore.sendAnalyticsEvent(closeAnalytics);
              prizeDropStore.closeGame();
              break;
            default:
              // activeKey is null: nothing was launched, so there is nothing to close. Falling through
              // to Prize Drop sent close analytics for a game that never opened, and called a closeGame()
              // whose setCloseDialog() could dismiss an unrelated dialog.
              break;
          }
        }
      } catch (error) {
        devError('MetagamesWarmFrame: failed to handle a message', error);
      }
    },
    [
      metagamesWarmStore,
      prizeDropStore,
      trailStore,
      hammersStore,
      puzzleStore,
      metagamesStore,
      walletStore,
      minigamesStore,
      startAnimation,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, [onMessage]);

  useEffect(() => {
    // Two gates, and both matter.
    //
    // newGameUiAuthEnabled: getGameUiURL builds version=2 until it resolves, and a version=2 document
    // takes the legacy Firebase path — a warm frame must never be booted onto it. It doubles as a
    // readiness signal, since it only flips once LaunchDarkly has answered.
    //
    // metagames_warm_launch: the kill switch. Defaults to FALSE, so this stays off until the flag is
    // created and turned on — a second permanent game-ui instance is not something to enable by accident.
    // Warming is the only control point needed: with no warm frame, launch() returns false and every
    // caller falls through to its existing dialog.
    //
    // WARM_LAUNCH_OVERRIDE_PARAM exists because the flag does not exist in LaunchDarkly yet, and testing
    // should not require creating it. Drop it once the flag is real.
    const overridden = new URLSearchParams(window.location.search).get(WARM_LAUNCH_OVERRIDE_PARAM) === '1';

    if (!gameUiStore.newGameUiAuthEnabled || !(overridden || resolveFlag('metagames_warm_launch', false))) {
      return;
    }

    // Never boot while a game is opening or running. A second game-ui competes for bandwidth and the
    // main thread with the slot's own boot, which delays the slots handshake that flips
    // gameStore.showGameUi — leaving the host header visible under game-ui's header, and tripping the
    // iframe watchdog into a reload. Visible only on a bundled build, where the slot normally wins that
    // race comfortably.
    if (window.location.pathname.startsWith('/games')) {
      return;
    }

    // And not during the lobby's own load either: warming is worthless if it steals bandwidth from the
    // page the player is actually looking at. Wait for an idle moment.
    const start = () => metagamesWarmStore.warm();

    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(start, { timeout: 5000 });

      return () => cancelIdleCallback(handle);
    }

    const timer = setTimeout(start, 3000);

    return () => clearTimeout(timer);
  }, [gameUiStore.newGameUiAuthEnabled, metagamesWarmStore]);

  const { src, isVisible, frameKey } = metagamesWarmStore;

  if (!src) {
    return null;
  }

  return (
    <div className={classNames(styles.cover, { [styles.hidden]: !isVisible })}>
      {isVisible && !isGameReady && <Loader cover />}
      <iframe
        // Bumped only when a frame has to be thrown away, to force a fresh element.
        key={frameKey}
        ref={iframeRef}
        allow="autoplay; clipboard-write *; clipboard-read *"
        className={styles.frame}
        data-testid="metagames-warm-frame"
        src={src}
        title="metagames"
      />
    </div>
  );
});
